"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { readCookie } from "helpers/Helper";
export const NavContext = createContext({});

function updateCssVars({ isMobile, isTablet, subscriberCookie }) {
  const root = document.documentElement.style;
  root.setProperty("--is-mobile", isMobile ? "1" : "0");
  root.setProperty("--is-tablet", isTablet ? "1" : "0");
  root.setProperty("--subscriberCookie", subscriberCookie ? "1" : "0");
}

export default function Provider({ children, ...props }) {
  const [value, setValue] = useState(props.value);
  const pathname = usePathname();

  const searchParams = useSearchParams();
  const li_did = searchParams.get("li_did");

  useEffect(() => {
    if (li_did) {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 30);

      const storageData = {
        value: li_did,
        expires: expirationDate.getTime(),
      };

      localStorage.setItem("li_did", JSON.stringify(storageData));
      setValue((prevState) => ({
        ...prevState,
        li_did: li_did,
      }));
    } else {
      const storedData = localStorage.getItem("li_did");
      if (storedData) {
        const { value, expires } = JSON.parse(storedData);
        if (new Date().getTime() < expires) {
          setValue((prevState) => ({
            ...prevState,
            li_did: value,
          }));
        } else {
          localStorage.removeItem("li_did");
        }
      }
    }
  }, [li_did]);

  useEffect(() => {
    setValue((prevState) => ({
      ...prevState,
      router: {
        ...prevState.router,
        asPath: pathname,
      },
    }));
  }, [pathname]);

  useEffect(() => {
    const handleCookieChange = () => {
      const ormtoken = readCookie("ormtoken");
      const subscribed = readCookie("subscribed");
      if (value.tokenCookie !== ormtoken || value.subscriberCookie !== subscribed) {
        updateCssVars({
          isMobile: value.isMobile,
          isTablet: value.isTablet,
          subscriberCookie: subscribed,
        });
        setValue((prevState) => ({
          ...prevState,
          tokenCookie: ormtoken,
          subscriberCookie: subscribed,
          hideEBBNl:
            ormtoken ||
            prevState.wasLoggedInCookie ||
            prevState.subscriberCookie ||
            prevState.isUserComingFromNL,
        }));
      }
    };

    const interval = setInterval(() => {
      handleCookieChange();
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const updatePageViewsSessionStorage = useCallback(() => {
    if (typeof window != "undefined") {
      if (window.sessionStorage.getItem("pageViews")) {
        let viewCount = parseInt(window.sessionStorage.getItem("pageViews"));
        window.sessionStorage.setItem("pageViews", viewCount + 1);
      } else {
        window.sessionStorage.setItem("pageViews", 1);
      }
    }
  }, []);

  const updateNewValue = useCallback((newVal) => {
    setValue((prevState) => ({
      ...prevState,
      ...newVal,
    }));
  }, []);

  const contextValue = useMemo(
    () => ({
      ...value,
      updatePageViews: updatePageViewsSessionStorage,
      setNewValue: updateNewValue,
    }),
    [value, updatePageViewsSessionStorage, updateNewValue]
  );

  return <NavContext.Provider value={contextValue}>{children}</NavContext.Provider>;
}

export function useAppContext() {
  return useContext(NavContext);
}
